import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useLang, tr } from "../contexts/LangContext";
import api from "../lib/api";
import { Calendar, MapPin, FileText, Trophy, ArrowLeft, Award, ListOrdered, X, Download } from "lucide-react";

const DOC_TABS = [
  { key: "notice_of_race",      icon: FileText,    color: "text-club-red"  },
  { key: "sailing_instructions",icon: FileText,    color: "text-navy"      },
  { key: "results",             icon: ListOrdered, color: "text-club-yellow-dark" },
  { key: "general_ranking",     icon: Award,       color: "text-green-700" },
];

export default function RegattaDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [r, setR] = useState(null);
  const [docs, setDocs] = useState([]);
  const [modal, setModal] = useState(null); // {key, label}

  useEffect(() => {
    api.get(`/regattas/${slug}`).then((res) => setR(res.data)).catch(() => {});
    api.get(`/regattas/${slug}/documents`).then((res) => setDocs(res.data)).catch(() => {});
  }, [slug]);

  if (!r) return <div className="pt-32 pb-20 text-center text-slate-500" data-testid="regatta-loading">{t.common.loading}</div>;

  const DOC_LABEL = {
    notice_of_race:       t.regattas.noticeOfRace,
    sailing_instructions: t.regattas.sailingInstructions,
    results:              t.regattas.results,
    general_ranking:      t.regattas.generalRanking || tr("Classement général"),
  };
  const byType = (key) => docs.filter((d) => d.doc_type === key);
  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid="regatta-detail">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
        <img src={r.image} alt={r.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/75 to-navy/40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 text-white">
          <Link to="/regattas" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-club-yellow mb-6 hover:text-white" data-testid="back-to-regattas">
            <ArrowLeft className="w-4 h-4" /> {t.nav.regattas}
          </Link>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-none font-light">{r.name}</h1>
          <div className="divider-gold mt-8" />
          <div className="flex flex-wrap gap-6 mt-6 text-sm text-slate-200">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-club-yellow" />
              {new Date(r.date).toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "numeric", month: "long", year: "numeric" })}
            </span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-club-yellow" />{r.location}</span>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-12">
          <p className="text-slate-700 text-lg leading-relaxed">{r[`description_${lang}`]}</p>
        </div>
      </section>

      {/* Documents (4 cards → pop-up) */}
      <section className="py-16 bg-sand">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="label-eyebrow text-club-red mb-3">Documents</div>
          <h2 className="font-serif text-3xl text-navy">{tr("Avis, instructions et classements")}</h2>
          <div className="divider-gold mt-4 mb-8" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {DOC_TABS.map(({ key, icon: Icon, color }) => {
              const items = byType(key);
              return (
                <button
                  key={key}
                  onClick={() => setModal({ key, label: DOC_LABEL[key] })}
                  className="text-left bg-white border border-slate-200 p-6 hover:border-club-red hover:shadow-lg transition-all group"
                  data-testid={`doc-card-${key}`}
                >
                  <Icon className={`w-8 h-8 ${color} mb-4`} strokeWidth={1.5} />
                  <div className="font-serif text-lg text-navy group-hover:text-club-red">{DOC_LABEL[key]}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-club-yellow-dark mt-3">
                    {items.length > 0 ? `${items.length} document${items.length > 1 ? "s" : ""}` : tr("Aucun document")}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {modal && <DocModal slug={slug} docKey={modal.key} label={modal.label} docs={byType(modal.key)} onClose={() => setModal(null)} backend={backend} lang={lang} />}
    </div>
  );
}

function DocModal({ slug, docKey, label, docs, onClose, backend, lang }) {
  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="doc-modal" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full p-8 relative my-12" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="doc-modal-close">
          <X className="w-5 h-5" />
        </button>
        <div className="label-eyebrow text-club-red mb-2">Régate · {slug}</div>
        <h2 className="font-serif text-3xl text-navy" data-testid="doc-modal-title">{label}</h2>
        <div className="divider-gold mt-4 mb-6" />

        {docs.length === 0 ? (
          <div className="text-slate-500 text-sm" data-testid="doc-modal-empty">{tr("Aucun document disponible pour l'instant.")}</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {docs.map((d) => (
              <li key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid={`doc-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-navy truncate">
                    {d.race_number > 0 && <span className="text-xs uppercase tracking-widest text-club-yellow-dark mr-2">Course #{d.race_number}</span>}
                    {d.title}
                  </div>
                  <div className="text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString(lang === "en" ? "en-GB" : lang)}</div>
                </div>
                <a
                  href={`${backend}${d.url}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-club-red hover:text-club-red-dark border border-club-red hover:border-club-red-dark px-3 py-2"
                  data-testid={`doc-dl-${d.id}`}
                >
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
